import type {
  VacationRequestsApiT,
  VacationRequestsMapperT,
} from "../types/vacationRequests.types";
import type { VacationRequestsRepositoryT } from "../../domain/ports/vacationRequests.repository";
import type {
  ApplyRequestsApi,
  ApplyRequestsMapperT,
} from "../types/applyRequests.types";

import { extractErrorMessage } from "utils/extractErrorMessage";

type VacationRequestsRepositoryParamsT = {
  vacationRequestsApi: VacationRequestsApiT;
  vacationRequestsMapper: VacationRequestsMapperT;
  applyRequestsApi: ApplyRequestsApi;
  applyRequestsMapper: ApplyRequestsMapperT;
};

export const createVacationRequestsRepository = ({
  vacationRequestsApi,
  applyRequestsApi,
  vacationRequestsMapper,
  applyRequestsMapper,
}: VacationRequestsRepositoryParamsT): VacationRequestsRepositoryT => ({
  getVacationRequests: async (colaboraInstanceId, filters) => {
    try {
      const response = await vacationRequestsApi.getVacationRequests(
        colaboraInstanceId,
        filters,
      );

      if (response.status !== 200) {
        throw new Error(
          "Ocurrió un error al obtener las solicitudes de vacaciones",
        );
      }

      return vacationRequestsMapper.vacationRequestsDtoToDomain(response.data);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Ocurrió un error al obtener las solicitudes de vacaciones",
      );

      throw new Error(message);
    }
  },
  getRequestDetail: async (requestId, colaboraInstanceId) => {
    try {
      const response = await vacationRequestsApi.getRequestDetail(
        requestId,
        colaboraInstanceId,
      );

      if (response.status !== 200) {
        throw new Error(
          "Ocurrió un error al obtener el detalle de la solicitud",
        );
      }

      return vacationRequestsMapper.requestDetailDtoToDomain(response.data);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Ocurrió un error al obtener el detalle de la solicitud",
      );

      throw new Error(message);
    }
  },
  getRequestFiles: async (requestId, colaboraInstanceId) => {
    try {
      const response = await vacationRequestsApi.getRequestFiles(
        requestId,
        colaboraInstanceId,
      );

      if (response.status !== 200) {
        throw new Error(
          "Ocurrió un error al obtener los archivos de la solicitud",
        );
      }

      return vacationRequestsMapper.requestFilesDtoToDomain(response.data);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Ocurrió un error al obtener los archivos de la solicitud",
      );

      throw new Error(message);
    }
  },
  updateVacationRequest: async (data, colaboraInstanceId) => {
    try {
      const dto = vacationRequestsMapper.toUpdateDto(data);

      const response = await vacationRequestsApi.updateVacationRequest(
        dto,
        colaboraInstanceId,
      );

      if (response.status !== 200 && response.status !== 201) {
        throw new Error("Ocurrió un error al actualizar la solicitud");
      }

      return vacationRequestsMapper.updateVacationRequestDtoToDomain(
        response.data,
      );
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Ocurrió un error al actualizar la solicitud",
      );

      throw new Error(message);
    }
  },
  applyRequests: async (requestIds) => {
    try {
      const body = applyRequestsMapper.toApplyRequestsDto(requestIds);

      const response = await applyRequestsApi.applyRequests(body);

      if (response.status !== 200 && response.status !== 201) {
        throw new Error(
          "Ocurrió un error al aplicar las solicitudes al sistema base",
        );
      }

      return applyRequestsMapper.applyRequestsDtoToDomain(response.data);
    } catch (error) {
      const message = extractErrorMessage(
        error,
        "Ocurrió un error al aplicar las solicitudes al sistema base",
      );

      throw new Error(message);
    }
  },
});
